import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { FadeIn } from '@/components/ui/AnimatedCard';
import { Modal } from '@/components/ui/Modal';
import { DeleteConfirmModal } from '@/components/shared/DeleteConfirmModal';
import { ListsList } from '@/components/lists/ListsList';
import { ListsFilters } from '@/components/lists/ListsFilters';
import { ItemManager } from '@/components/lists/ItemManager';
import { QuickItemEntry } from '@/components/lists/QuickItemEntry';
import { ListForm } from '@/components/lists/ListForm';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { useNavigationStore } from '@/store/navigationStore';
import { List } from '@/types/lists';

export function Lists() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract context datetime from URL query params (for calendar event navigation)
  // Supports both 'datetime' (with time) and legacy 'date' (date only) params
  const contextDateTimeString = searchParams.get('datetime');
  const contextDateString = searchParams.get('date');
  const contextDate = contextDateTimeString
    ? new Date(contextDateTimeString) // Full ISO datetime
    : contextDateString
      ? new Date(contextDateString + 'T00:00:00') // Legacy: date only, parse as local midnight
      : new Date(); // Default: now

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuickItemEntry, setShowQuickItemEntry] = useState(false);
  const [editingList, setEditingList] = useState<List | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<List | null>(null);

  // Lists store
  const {
    lists,
    filteredLists,
    isLoading,
    error,
    filters,
    sort,
    initializeLists,
    deleteList,
    reorderLists,
    setFilters,
    setSort,
    clearFilters
  } = useListsStore();

  // Goals store for linking
  const { goals, initializeGoals } = useGoalsStore();

  // Aspirations store for linking
  const { aspirations, initializeAspirations } = useAspirationsStore();

  // Navigation store for header
  const { setPageInfo } = useNavigationStore();

  // Find the selected list from URL params
  const selectedList = listId ? filteredLists.find(list => list.id === listId) : null;

  // Debug logging
  useEffect(() => {
    if (listId) {
      if (!selectedList && filteredLists.length > 0) {
        console.error('❌ List not found!', {
          searchingFor: listId,
          availableIds: filteredLists.map(l => l.id),
          exactMatch: filteredLists.find(l => l.id === listId)
        });
      }
    }
  }, [listId, filteredLists, selectedList, isLoading]);

  // Initialize data and read URL params
  useEffect(() => {
    initializeLists();
    initializeGoals();
    initializeAspirations();

    // Read goalId from URL params and apply filter
    const goalId = searchParams.get('goalId');
    if (goalId) {
      setFilters({ goalId: [goalId] });
    }
  }, [initializeLists, initializeGoals, initializeAspirations, searchParams, setFilters]);

  // Set page info for header
  useEffect(() => {
    if (selectedList) {
      // When viewing item details, show the list title and new item button
      setPageInfo(selectedList.title, {
        label: 'New Item',
        icon: Plus,
        onClick: () => setShowQuickItemEntry(true)
      });
    } else {
      // When viewing lists, show normal Lists header
      setPageInfo('Lists', {
        label: 'New List',
        icon: Plus,
        onClick: openCreateModal
      });
    }
  }, [setPageInfo, selectedList]);


  // Get available goals for linking
  const availableGoals = goals.map(goal => ({
    id: goal.id,
    title: goal.title
  }));

  // Get available aspirations for filtering
  const availableAspirations = aspirations.map(aspiration => ({
    id: aspiration.id,
    title: aspiration.title
  }));

  // Calculate final filtered lists (including aspiration filtering)
  const listsToShow = useMemo(() => {
    let result = [...filteredLists];

    // Additional client-side filtering for aspirations (since it requires goal data)
    if (filters.aspirationId && filters.aspirationId.length > 0) {
      const relevantGoalIds = goals
        .filter(goal => filters.aspirationId!.includes(goal.aspirationId))
        .map(goal => goal.id);

      result = result.filter(list =>
        list.goalId && relevantGoalIds.includes(list.goalId)
      );
    }

    return result;
  }, [filteredLists, filters.aspirationId, goals]);

  const handleListEdit = (list: List) => {
    console.log('Edit list:', list);
    setEditingList(list);
    setShowEditModal(true);
  };

  const handleListClick = (list: List) => {
    console.log('🔗 Navigating to list:', {
      listId: list.id,
      listTitle: list.title,
      url: `/list-items/${list.id}`
    });
    navigate(`/list-items/${list.id}`);
  };

  const handleListDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteList(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete list:', error);
    }
  };

  // Calculate cascade info for delete confirmation
  const cascadeInfo = useMemo(() => {
    if (!deleteConfirm) return [];
    return [
      { label: 'list item', count: deleteConfirm.items?.length || 0 }
    ];
  }, [deleteConfirm]);

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading lists...</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (error) {
      return (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-red-600">
              <p>Error loading lists: {error}</p>
              <Button
                variant="outline"
                onClick={initializeLists}
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Templates are stored separately, so filteredLists only contains regular lists
    // listsToShow is pre-calculated above via useMemo

    if (listsToShow.length === 0) {
      return (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              title="No lists yet"
              description="Create actionable lists to break down your goals into manageable tasks and action items."
              action={{
                label: "Create Your First List",
                onClick: () => setShowCreateModal(true)
              }}
            />
          </CardContent>
        </Card>
      );
    }

    // Check if exactly one goal is selected for drag and drop
    const isSingleGoalSelected = filters.goalId?.length === 1;
    const shouldShowDragHandles = isSingleGoalSelected;

    return (
      <ListsList
        lists={listsToShow}
        onEdit={handleListEdit}
        onDelete={setDeleteConfirm}
        onReorder={reorderLists}
        onItemClick={handleListClick}
        showDragHandles={shouldShowDragHandles}
      />
    );
  };

  return (
    <Container size="full" className="py-6">
      <Stack spacing="lg">
        {/* Show Item Manager if a list is selected */}
        {selectedList ? (
          <FadeIn>
            <ItemManager list={selectedList} contextDate={contextDate} />
          </FadeIn>
        ) : (
          <>
            {/* Filters */}
            <FadeIn>
              <ListsFilters
                filters={filters}
                sort={sort}
                onFiltersChange={setFilters}
                onSortChange={setSort}
                onClearFilters={clearFilters}
                availableGoals={availableGoals}
                availableAspirations={availableAspirations}
                totalCount={lists.length}
                filteredCount={listsToShow.length}
              />
            </FadeIn>

            {/* Content */}
            <FadeIn>
              {renderContent()}
            </FadeIn>
          </>
        )}
      </Stack>

      {/* Create List Form */}
      <ListForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        selectedGoalId={filters.goalId?.length === 1 ? filters.goalId[0] : undefined}
      />

      {/* Edit List Form */}
      <ListForm
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingList(null);
        }}
        listId={editingList?.id}
        isEditing={true}
      />

      {/* Quick Item Entry Modal */}
      {selectedList && (
        <Modal
          isOpen={showQuickItemEntry}
          onClose={() => setShowQuickItemEntry(false)}
          title={`Add Item to ${selectedList.title}`}
        >
          <QuickItemEntry
            listId={selectedList.id}
            placeholder="Enter item content..."
            onItemAdded={() => setShowQuickItemEntry(false)}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleListDelete}
        title="Delete List"
        itemName={deleteConfirm?.title || ''}
        itemType="list"
        description="All items in this list will be permanently deleted."
        cascadeInfo={cascadeInfo}
      />
    </Container>
  );
}
